<template>
  <div v-if="valueData" class="proof-of-value">
    <p class="back">
      <span @click="$emit('close')"><a-icon type="left" /><span :class="[lang]">{{ $t("POVBack") }}</span></span>
    </p>
    <section class="proof-of-value">
      <!-- ============ Overview ============ -->
      <section class="overview-card">
        <div class="overview-head">
          <span class="check-mark">✅</span>
          <h1 data-v-46e5a178=""><strong>"{{ valueData.campaign_name }}"</strong> is ready — see what it'll deliver
            before you launch</h1>
        </div>
        <p class="overview-sub">{{ $t("POVOverviewSub") }}</p>
      </section>

      <!-- ============ Retrieval progress ============ -->
      <div class="divider">
        <section class="progress-card" :class="{hide:loading1==3 && loading2==3}">
          <div class="progress-head">
            <div class="progress-title">
              <span class="head-spinner"></span>
              <h3>{{ $t("POVLoadingTitle") }}</h3>
            </div>
            <div class="progress-timer">
              <div class="timer-value">{{ $t("POVLoadingTimer") }}</div>
              <div class="timer-sub">{{ $t("POVLoadingTimerSub") }}</div>
            </div>
          </div>

          <p class="progress-sub">{{ $t("POVLoadingDesc") }}</p>

          <div class="progress-track">
            <div class="progress-fill"></div>
          </div>

          <ul class="progress-steps" >
            <li class="doing">
              <span class="step-num" v-if='loading1==2'>1</span>
              <i  v-if='loading1==3' class="anticon anticon-check-circle"><svg viewBox="64 64 896 896" data-icon="check-circle" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 0 1-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z"></path></svg></i>
              <div class="step-info">
                <div class="step-name">{{ $t("POVLoadingStep1") }}</div>
                <div class="step-desc">{{ $t("POVLoadingStep1Desc") }}</div>
              </div>
              <span class="step-badge doing" v-if='loading1==2'>{{ $t("POVLoadingBadgeIn") }}</span>
              <span class="step-badge doing" v-if='loading1==3'>Complete</span>
            </li>
            <li :class="{wait:loading2==1, doing:loading2==2 || loading2==3}">
              <span class="step-num" v-if='loading2==2 || loading2==1'>2</span>
               <i  v-if='loading2==3' class="anticon anticon-check-circle"><svg viewBox="64 64 896 896" data-icon="check-circle" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false" class=""><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 0 1-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z"></path></svg></i>
              <div class="step-info">
                <div class="step-name">{{ $t("POVLoadingStep2") }}</div>
                <div class="step-desc">{{ $t("POVLoadingStep2Desc") }}</div>
              </div>
              <span class="step-badge wait" v-if='loading2==1'>{{ $t("POVLoadingBadgeWait") }}</span>
               <span class="step-badge doing" v-if='loading2==2'>{{ $t("POVLoadingBadgeIn") }}</span>
              <span class="step-badge doing" v-if='loading2==3'>Complete</span>
            </li>
          </ul>

          <div class="progress-note">
            <p>{{ $t("POVLoadingNote") }}</p>
            <a-button class="skip-btn" size="large" @click="$emit('publishDirectly')">
              {{ $t("POVLoadingSkipBtn") }}
            </a-button>
          </div>
        </section>

        <section class="step-card" v-if='loading1==1 || loading1==2'>
          <header class="step-title">
            <span class="step-num">1</span>
            <h2>{{ $t("POVLoadingCard1Title") }}</h2>
            <span class="head-status doing"><i class="status-dot"></i>{{ $t("POVLoadingCard1Status") }}</span>
          </header>
          <div class="loading-body">
            <span class="body-spinner"></span>
            <div class="loading-text">
              <h4>{{ $t("POVLoadingCard1Msg") }}</h4>
              <p>{{ $t("POVLoadingCard1Sub") }}</p>
            </div>
          </div>
        </section>

       
      </div>

      <!-- ============ Step 1: Candidates ============ -->

      <section class="step-card" v-if='loading1==3'>
        <header class="step-title">
          <span class="step-num">1</span>
          <h2>About to reach out to the <strong class="hl-blue">{{
            valueData.target_preview ? valueData.target_preview.target_count : 0
              }}</strong> {{ valueData.campaign_type == 1 ? 'customers' : 'candidates' }} from the URL you entered</h2>
        </header>

        <p class="source-line" v-html="$t('POVSourceLine')"></p>

        <div class="url-pill">
          <a-icon type="link" />
          {{ valueData.search_url }}
        </div>

        <ul class="candidate-list" >
          <li v-for="(item, index) in candidateList" :key="index" class="candidate-item">
            <span v-if="!item.head_img" class="avatar" :style="{ background: 'rgb(14, 117, 106)' }">
              {{ getFirstCharUpper(item.user_name)}}
            </span>
            <span v-else class="avatar"  :style="{ backgroundImage: `url(${item.head_img})`  }">
            </span>
            <div class="candidate-meta">
              <div class="candidate-name">{{ item.user_name }}</div>
              <div class="candidate-role">{{ item.job_title }}</div>
            </div>
            <span class="status-tag">{{ $t("POVStatusTag") }}</span>
          </li>
        </ul>

        <p class="footnote" v-html="$t('POVStep1Footnote')"></p>
      </section>

      <div class="divider" v-if='loading2==1 || loading2==2'>
         <section class="step-card"  >
          <header class="step-title">
            <span class="step-num">2</span>
            <h2>{{ $t("POVLoadingCard2Title") }}</h2>
            <span v-if='loading2==1' class="head-status wait">{{ $t("POVLoadingCard2Status") }}</span>
            <span v-if='loading2==2' class="head-status doing"><i class="status-dot"></i>In progress about 1 min</span>

          </header>
          <div class="loading-body">
            <span class="body-spinner"></span>
            <div class="loading-text">
              <h4>{{ $t("POVLoadingCard2Msg") }}</h4>
              <p>{{ $t("POVLoadingCard2Sub") }}</p>
            </div>
          </div>
        </section>
      </div>

      

      <!-- ============ Step 2: AI conversation ============ -->
      <section class="step-card" v-if="loading2==3">
        <header class="step-title">
          <span class="step-num">2</span>
          <h2>
            {{ $t("POVStep2Title") }}
            <small>{{ $t("POVStep2Note") }}</small>
          </h2>
        </header>

        <div class="conv-container" >
          <template v-for="(item, index) in talkLiST" >
              <div class="conv-head" :key="index">
                <span class="avatar" :style="{ background: '#0e756a' }">{{getFirstCharUpper(item.profile
.user_name)}}</span>
                <div class="conv-head-meta">
                  <div class="candidate-name">{{item.profile
.user_name}}</div>
                  <div class="candidate-role">
                    {{item.profile.job_title}}
                  </div>
                </div>
                <span class="warn-tag">{{ $t("POVWarnTag") }}</span>
              </div>

              <div class="chat-area" :key="index+'chat'">
                <div v-for="(msg, idx) in item.messages" :key="idx" class="chat-line" :class="{ mine: msg.sender === 'candidate' }">
                  <span v-if="msg.sender === 'agent'" class="chat-avatar">AI</span>
                  <div class="chat-bubble">{{ msg.content }}</div>
                  <span v-if="msg.sender === 'candidate'" class="chat-avatar me">{{getFirstCharUpper(item.profile
.user_name)}}</span>
                </div>
              </div>
          </template>
        </div>

       

        <p class="footnote footnote-note">{{ $t("POVStep2Footnote") }}</p>
      </section>

      <!-- ============ Step 3: Expected results ============ -->
      <section class="step-card result-step">
        <header class="step-title">
          <span class="step-num">3</span>
          <h2>{{ $t("POVStep3Title") }}</h2>
        </header>

        <div class="result-grid">
          <div class="result-card reply">
            <div class="result-label">{{ $t("POVResultReplyLabel") }}</div>
            <div class="result-value">{{ $t("POVResultReplyValue") }}</div>
          </div>
          <div class="result-card interest">
            <div class="result-label">{{ $t("POVResultInterestLabel") }}</div>
            <div class="result-value">20 +</div>
          </div>
        </div>
      </section>

      <!-- ============ Safe-tip ============ -->
      <section class="safe-tip">
        <a-icon type="safety" />
        <span v-html="$t('POVSafeTip')"> </span>
      </section>

      <!-- ============ Bottom action ============ -->
      <section class="action-bar">
        <div class="action-left">
          <div class="action-title">
            {{ $t("POVActionTitle") }}
          </div>
          <div class="action-sub">
            {{ $t("POVActionSub") }}
          </div>
        </div>
        <div class="action-right">
          <a-button class="adjust-btn" @click="$emit('close')" size="large">
            {{ $t("POVAdjustBtn") }}
          </a-button>
          <a-button type="primary" class="confirm-btn" size="large" @click="$emit('publishDirectly')">
            {{ $t("POVConfirmBtn") }}
          </a-button>
        </div>
      </section>
    </section>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "ProofOfValue",
  emits: ["back", "adjust", "confirm"],
  i18n: require("./proofOfValuei18n"),
  props: ['valueData'],
  computed: {
    ...mapState("setting", ["lang"]),
  },
  methods: {
    getFirstCharUpper(s){
      return s.trimStart().charAt(0).toUpperCase() || '';
    },
    startProfilePreview() {
      this.loading1 = 2;
      this.$axios(
        "/linkedin/campaign_profile_preview",
        { campaign_id: parseInt(this.$route.query.campaign_id) },
        "post"
      ).then((res) => {
        const data = res.data || {};
        if (res.code !== 200) return this.profilePreviewFailed();
        if (data.status === "success") return this.finishProfilePreview(data);
        if (data.task_id && ["pending", "running"].includes(data.status)) {
          return this.pollProfilePreview(data.task_id);
        }
        this.profilePreviewFailed();
      }).catch((error) => {
        console.log(error);
        this.profilePreviewFailed();
      });
    },
    pollProfilePreview(taskId) {
      this.profileTaskTimer = setTimeout(() => {
        this.$axios(
          "/linkedin/campaign_profile_preview_status",
          { task_id: taskId },
          "post"
        ).then((res) => {
          const data = res.data || {};
          if (res.code === 200 && data.status === "success") {
            return this.finishProfilePreview(data);
          }
          if (res.code === 200 && ["pending", "running"].includes(data.status)) {
            return this.pollProfilePreview(taskId);
          }
          this.profilePreviewFailed();
        }).catch((error) => {
          console.log(error);
          this.profilePreviewFailed();
        });
      }, 2000);
    },
    finishProfilePreview(data) {
      this.candidateList = data.profiles || [];
      this.loading1 = 3;
      this.loading2 = 2;
      this.$axios(
        "/linkedin/campaign_simulation_conversations",
        { campaign_id: parseInt(this.$route.query.campaign_id) },
        "post"
      ).then((res) => {
        if (res.code === 200) {
          this.talkLiST = (res.data || {}).conversations || [];
          this.loading2 = 3;
        } else {
          this.loading2 = 1;
        }
      }).catch((error) => {
        console.log(error);
        this.loading2 = 1;
      });
    },
    profilePreviewFailed() {
      clearTimeout(this.profileTaskTimer);
      this.loading1 = 1;
      this.loading2 = 1;
    }
  },
  data() {
    return {
      //加载第一个接口
      loading1:1,
      //加载第二个接口 ，1等待加载 2正在加载 3完成请求
      loading2:1,
      candidateList:[],
      talkLiST:[],
      candidates: [
        {
          name: "David R.",
          role: "Senior Frontend Engineer · North America",
          initial: "D",
          color: "#0e756a",
        },
        {
          name: "Lena P.",
          role: "Full-Stack Developer · Remote",
          initial: "L",
          color: "#0e756a",
        },
        {
          name: "Kevin H.",
          role: "React Engineer · SaaS",
          initial: "K",
          color: "#0e756a",
        },
      ],
      chatTranscript: [
        {
          from: "ai",
          text: "Hi David, your senior frontend work with React looks really strong. We're a global SaaS company hiring a Senior Frontend (fully remote / competitive pay) — open to a 15-min chat?",
        },
        {
          from: "me",
          text: "Sure, tell me more about the role and the team.",
        },
        {
          from: "ai",
          text: "A 12-person team, React + TypeScript + Next.js, building for North American SMBs. You'd own the frontend architecture, reporting to the Head of Eng. $120k–150k + equity, fully remote.",
        },
        {
          from: "me",
          text: "Sounds solid. What's the interview process and timeline?",
        },
        {
          from: "ai",
          text: "Light process: a 30-min intro → one technical round → a team chat, usually wrapped in 1–2 weeks. Which day this week works for an intro?",
        },
      ],
      profileTaskTimer: null,
    };
  },
  created() {
    this.startProfilePreview();
  },
  beforeDestroy() {
    clearTimeout(this.profileTaskTimer);
  }
};
</script>

<style lang="less" scoped>
  .hide{display: none;}
.anticon {
    display: inline-block;
      color: #0e756a;
    font-size: 16px;
    font-style: normal;
    line-height: 0;
    text-align: center;
    text-transform: none;
    vertical-align: -0.125em;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
}
.back {
  border-top: #d1d1d1 solid 1px;
  color: #0e756a;
  left: 254px;
  position: fixed;
  top: 64px;
  right: 0;
  z-index: 999;
  height: 50px;
  line-height: 50px;
  padding-left: 30px;
  background: #fff;

  span {
    cursor: pointer;
  }
}

.proof-of-value {
  margin: 0 auto;
  padding: 20px 30px;
  color: #1f1f1f;
}

// ---------- Back link ----------
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  color: #0e756a;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;

  &:hover {
    color: #0a5d54;
  }
}

// ---------- Reusable card ----------
.overview-card,
.step-card,
.safe-tip,
.action-bar {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;
}

// ---------- Overview ----------
.overview-card {
  .overview-head {
    display: flex;
    align-items: flex-start;
    gap: 10px;

    .check-mark {
      font-size: 18px;
      line-height: 1.4;
      margin-top: 2px;
    }

    h1 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
      line-height: 1.5;

      strong {
        font-weight: 600;
      }
    }
  }

  .overview-sub {
    margin: 10px 0 0 28px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.55);
  }
}

// ---------- Divider: retrieval progress ----------
.divider {
  .progress-card {
    background: linear-gradient(180deg, #e6f4f1 0%, #ffffff 70%);
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 16px;
  }

  .progress-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .progress-title {
    display: flex;
    align-items: center;
    gap: 10px;

    h3 {
      margin: 0;
      font-size: 17px;
      font-weight: 600;
      color: #1f1f1f;
    }
  }

  .head-spinner {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border: 3px solid #e5e7eb;
    border-top-color: #0e756a;
    border-radius: 50%;
    animation: pov-spin 0.9s linear infinite;
  }

  .progress-timer {
    flex-shrink: 0;
    text-align: end;

    .timer-value {
      font-size: 14px;
      font-weight: 600;
      color: #0e756a;
    }

    .timer-sub {
      margin-top: 2px;
      font-size: 11px;
      color: rgba(0, 0, 0, 0.45);
    }
  }

  .progress-sub {
    margin: 8px 0 0;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1.6;
  }

  .progress-track {
    height: 6px;
    margin: 14px 0 18px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
    display:none;

    .progress-fill {
      width: 35%;
      height: 100%;
      background: #0e756a;
      border-radius: 3px;
    }
  }

  .progress-steps {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 0;
      .step-num {
        display: inline-block;
        width: 24px;
        height: 24px;
        line-height: 24px;
        text-align: center;
        background: #f0f0f0;
        color: rgba(0, 0, 0, 0.45);
        border-radius: 50%;
        font-size: 13px;
        font-weight: 600;
      }

      &.doing .step-num {
        background: #e6f4f1;
        color: #0e756a;
      }

      & + li {
        border-top: 1px solid #f0f0f0;
      }
    }

    .step-info {
      flex: 1;
      min-width: 0;
    }

    .step-name {
      font-size: 14px;
      font-weight: 500;
      color: #1f1f1f;
    }

    .step-desc {
      margin-top: 2px;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.55);
    }

    .step-badge {
      flex-shrink: 0;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 500;

      &.doing {
        background: #e6f4f1;
        color: #0e756a;
      }

      &.wait {
        background: #fff3e6;
        color: #d46b08;
      }
    }
  }

  .progress-note {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 14px;
    padding: 12px 16px;
    // background: #f6f8fa;
    border-radius: 8px;

    p {
      flex: 1;
      margin: 0;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.55);
      line-height: 1.6;
    }

    .skip-btn {
      flex-shrink: 0;
      background: #fff;
      border-color: #e6f4f1;

      &:hover,
      &:focus {
        background: #fff;
        border-color: #e6f4f1;
      }
    }
  }

  .head-status {
    flex-shrink: 0;
    margin-left: auto;
    align-self: center;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;

    &.doing {
      color: #0e756a;
    }

    &.wait {
      padding: 2px 8px;
      background: #fff3e6;
      color: #d46b08;
      border-radius: 4px;
    }

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #0e756a;
      animation: pov-blink 1.2s ease-in-out infinite;
    }
  }

  .loading-body {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 18px;
    background: #f6f8fa;
    border-radius: 8px;

    .body-spinner {
      flex-shrink: 0;
      width: 22px;
      height: 22px;
      margin-top: 1px;
      border: 3px solid #e5e7eb;
      border-top-color: #0e756a;
      border-radius: 50%;
      animation: pov-spin 0.9s linear infinite;
    }

    .loading-text {
      flex: 1;
      min-width: 0;

      h4 {
        margin: 0 0 4px;
        font-size: 14px;
        font-weight: 600;
        color: #1f1f1f;
        line-height: 1.5;
      }

      p {
        margin: 0;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.55);
        line-height: 1.6;
      }
    }
  }
}

@keyframes pov-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pov-blink {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.35;
  }
}

// ---------- Step header ----------
.step-title {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;

  .step-num {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    line-height: 24px;
    text-align: center;
    background: #0e756a;
    color: #fff;
    border-radius: 50%;
    font-size: 13px;
    font-weight: 600;
  }

  h2 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.6;

    strong {
      font-weight: 600;
    }

    .hl-blue {
      color: #0e756a;
    }

    small {
      display: inline;
      font-size: 13px;
      font-weight: 400;
      color: rgba(0, 0, 0, 0.55);
      margin-left: 4px;
    }
  }
}

// ---------- Step 1: Candidates ----------
.source-line {
  margin: 0 0 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);

  strong {
    font-weight: 600;
    color: #1f1f1f;
  }
}

.url-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f6f8fa;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 16px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.candidate-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid #f0f0f0;

  .candidate-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #0e756a;
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-size: 100%;
    background-position: center;
    background-repeat: no-repeat;
  }

  .candidate-meta {
    flex: 1;
    min-width: 0;
  }

  .candidate-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f1f1f;
    line-height: 1.3;
  }

  .candidate-role {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
    margin-top: 2px;
  }

  .status-tag {
    flex-shrink: 0;
    padding: 2px 8px;
    background: #e6f4f1;
    color: #0e756a;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
  }
}

.footnote {
  margin: 12px 0 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.6;

  strong {
    font-weight: 600;
    color: #1f1f1f;
  }

  &.footnote-note {
    margin-top: 14px;
  }
}

// ---------- Step 2: Conversation ----------
.conv-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  .avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    color: #fff;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .conv-head-meta {
    flex: 1;
  }

  .warn-tag {
    flex-shrink: 0;
    padding: 4px 10px;
    background: #fff3e6;
    color: #d4380d;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
  }
}

.chat-area {
  max-height: 320px;
  overflow-y: auto;
  padding: 8px 4px;

  .chat-line {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 14px;

    &.mine {
      justify-content: flex-end;

      .chat-bubble {
        background: #e6f4f1;
        color: #1f1f1f;
      }
    }
  }

  .chat-avatar {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f0f0f0;
    color: rgba(0, 0, 0, 0.55);
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;

    &.me {
      background: #0e756a;
      color: #fff;
    }
  }

  .chat-bubble {
    max-width: 80%;
    padding: 10px 14px;
    background: #f6f8fa;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.6;
    color: #1f1f1f;
  }
}

// ---------- Step 3: Results ----------
.result-step {
  .result-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .result-card {
    padding: 16px 18px;
    border-radius: 10px;

    &.reply {
      background: #f0fbf6;
      border: 1px solid #c8e8d6;
    }

    &.interest {
      background: #fff3e6;
      border: 1px solid #ffd9b3;
    }

    .result-label {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.55);
      margin-bottom: 8px;
    }

    .result-value {
      font-size: 20px;
      font-weight: 700;
      color: #1f1f1f;
    }
  }
}

// ---------- Safe tip ----------
.safe-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.65);
  background: #f6f8fa;

  .anticon {
    margin-top: 2px;
    color: #0e756a;
  }

  strong {
    font-weight: 600;
    color: #1f1f1f;
  }
}

// ---------- Action bar ----------
.action-bar {
  display: flex;
  align-items: center;
  gap: 16px;

  .action-left {
    flex: 1;
  }

  .action-title {
    font-size: 14px;
    font-weight: 600;
    color: #1f1f1f;
  }

  .action-sub {
    margin-top: 4px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1.5;
  }

  .action-right {
    flex-shrink: 0;
    display: flex;
    gap: 10px;
  }

  .adjust-btn {
    color: #1f1f1f;
    border: 1px solid #d9d9d9;
    background: #fff;

    &:hover {
      color: #0e756a;
      border-color: #0e756a;
    }
  }

  .confirm-btn {
    background: #ff6a00;
    border-color: #ff6a00;

    &:hover,
    &:focus {
      background: #e85f00;
      border-color: #e85f00;
    }
  }
}
</style>
